/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useCallback, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { getTimeString } from '../../utils/time';

// Balances come off the API in seconds; getTimeString formats minutes.
const formatSeconds = (seconds) => getTimeString(Number(seconds || 0) / 60);

const SOURCE_LABELS = { manual_edit: 'Edited', import: 'Imported' };

function BalanceEditModal({ match, getMembers, onClose }) {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const oldBalance = match.balance / 60;
  const hours = Math.floor(oldBalance / 60) < 10 ? `0${Math.floor(oldBalance / 60)}` : Math.floor(oldBalance / 60);
  const minutes = oldBalance % 60 < 10 ? `0${oldBalance % 60}` : oldBalance % 60;
  const [balance, setBalance] = useState(`${hours}:${Number(minutes).toFixed(2)}`);
  const [balanceValue, setBalanceValue] = useState(0);
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  // Every manual adjustment is recorded server-side; show the trail for this
  // member/policy right where the next adjustment is about to be made.
  const getHistory = useCallback(async () => {
    if (!activeWorkspace || !match.memberId) {
      setIsLoadingHistory(false);
      return;
    }
    setIsLoadingHistory(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getPTOBalanceHistory(activeWorkspace.id, match.memberId),
      params: { policyId: match.policyId, limit: 20 },
    });
    if (response.success) setHistory(response.data?.history ?? []);
    setIsLoadingHistory(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace, match.memberId, match.policyId]);

  useEffect(() => { getHistory(); }, [getHistory]);

  const handleBalance = (e) => {
    const { value } = e.target;
    setBalance(value);
    const parts = value.split(':');
    if (parts[0] && parts[1]) setBalanceValue(parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10));
    else setBalanceValue(0);
  };

  const handleEdit = async () => {
    if (!activeWorkspace) return;
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.editOffTimeBalance(activeWorkspace.id),
      data: { balance: match, balanceValue, reason },
    });
    if (response.success) {
      displayNotification({ message: 'Edit the balance value successfully.', severity: 'success' });
      if (onClose) onClose();
      getMembers();
    } else {
      displayNotification({ message: 'An error occurs while editing data.', severity: 'error' });
    }
  };

  const inputCls = 'w-full box-border px-3 py-2 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500';

  let historyContent = <span className="block text-xs text-ink-500">Loading…</span>;
  if (!isLoadingHistory && !history.length) {
    historyContent = <span className="block text-xs text-ink-500">No manual changes yet.</span>;
  } else if (!isLoadingHistory) {
    historyContent = (
      <div className="flex flex-col gap-2.5" style={{ maxHeight: 168, overflowY: 'auto' }}>
        {history.map((entry) => (
          <div key={entry.id}>
            <span className="block text-xs text-ink-900 tabular-nums">
              {`${formatSeconds(entry.previousBalanceSeconds)} \u2192 ${formatSeconds(entry.newBalanceSeconds)}`}
              <span className="text-ink-500">
                {` \u00b7 ${SOURCE_LABELS[entry.source] || entry.source}`}
              </span>
            </span>
            <span className="block text-[11px] text-ink-500" style={{ marginTop: 1 }}>
              {`${entry.changedByName || 'Unknown'} \u00b7 ${moment(entry.createdAt).format('MMM D, YYYY h:mm A')}`}
            </span>
            {entry.reason ? (
              <span className="block text-[11px] text-ink-700" style={{ marginTop: 2 }}>{entry.reason}</span>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-[12px] border border-rule"
      style={{ position: 'absolute', top: 38, left: 8, width: 300, padding: 16, borderStyle: 'solid', zIndex: 10, boxShadow: '0 8px 24px rgba(14, 30, 64, 0.12)', textAlign: 'left' }}
    >
      <div className="mb-3">
        <span className="block text-sm font-medium text-ink-900 mb-1.5">Amount</span>
        <input value={balance} onChange={handleBalance} className={inputCls} style={{ borderStyle: 'solid' }} placeholder="HH:MM" />
      </div>
      <div className="mb-3">
        <span className="block text-sm font-medium text-ink-900 mb-1.5">Why are you modifying this?</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Required" className={inputCls} style={{ borderStyle: 'solid', minHeight: 80, resize: 'vertical' }} />
      </div>
      <button
        type="button"
        onClick={handleEdit}
        disabled={!balanceValue || !reason}
        className="inline-flex items-center justify-center box-border h-10 w-full px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        Apply
      </button>

      <div className="border-t border-rule" style={{ borderTopStyle: 'solid', marginTop: 16, paddingTop: 12 }}>
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-2">Change history</span>
        {historyContent}
      </div>
    </div>
  );
}

BalanceEditModal.propTypes = {
  match: PropTypes.shape({
    id: PropTypes.number.isRequired,
    balance: PropTypes.number.isRequired,
    policyId: PropTypes.number.isRequired,
    memberId: PropTypes.number,
  }).isRequired,
  getMembers: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

BalanceEditModal.defaultProps = { onClose: () => {} };

export default BalanceEditModal;
